import { useEffect, useMemo, useState } from "react";
import { formatDate } from "../../shared/utility";
import { kgFmt as crKgFmt, lbsFmt, pcsFmt as crPcsFmt } from "../../shared/formatting";
import SelectMenu from "../../components/controls/SelectMenu";
import CreditReturnItemsTable from "../../components/traceability/CreditReturnItemsTable";

// PrimeReact
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";

const kgFmt = (v) => (v == null ? "—" : `${Number(v).toFixed(2)} kg`);
const pcsFmt = (v) => (v == null ? "—" : `${Number(v)} pcs`);
const dollarFmt = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);
const sum = (arr, sel) => arr.reduce((a, r) => a + (Number(sel(r)) || 0), 0);

// -------------------- Mapping helpers --------------------
function mapFirstLayerRows(byParent) {
  return (byParent || []).map((r, idx) => ({
    key: `root:${r.parent_wip_id === -1 ? "recv" : r.parent_wip_id}:${idx}`,
    kind: r.parent_wip_id && r.parent_wip_id !== -1 ? "wip" : "receiving",
    id: r.parent_wip_id && r.parent_wip_id !== -1 ? r.parent_wip_id : null,
    label:
      r.parent_wip_id && r.parent_wip_id !== -1
        ? `${r.wip_lot_number || ""}`.trim()
        : `${r.receiving_lot_number || ""}`.trim(),
    incomingKg: r.incoming_kg, // incoming into CHILD WIP for the chosen PLU
    incomingPcs: r.incoming_pcs,
    outgoingKg: r.parent_produced_total_kg ?? null, // parent produced total for PLU (first layer only)
    outgoingPcs: r.parent_produced_total_pcs ?? null, // parent produced total pcs for PLU (first layer only)
    receivingId: r.receiving_invoice_id || null, // for View column links
    receivingInvoiceNumber: r.receiving_invoice_number || null,
    // expansion data (lazy)
    children: null, // for deeper parents on expand
  }));
}

function mapDeeperRows(directParents) {
  return (directParents || []).map((r, idx) => ({
    key: `deep:${r.parent_wip_id === -1 ? "recv" : r.parent_wip_id}:${idx}`,
    kind: r.parent_wip_id && r.parent_wip_id !== -1 ? "wip" : "receiving",
    id: r.parent_wip_id && r.parent_wip_id !== -1 ? r.parent_wip_id : null,
    label:
      r.parent_wip_id && r.parent_wip_id !== -1
        ? `${r.wip_lot_number || ""}`.trim()
        : `${r.receiving_lot_number || ""}`.trim(),
    incomingKg: r.incoming_kg, // incoming into the CURRENT (expanded) WIP
    incomingPcs: r.incoming_pcs ?? null,
    outgoingKg: r.parent_produced_total_kg ?? null, // parent produced total for expanded WIP
    outgoingPcs: r.parent_produced_total_pcs ?? null, // parent produced total pcs for expanded WIP
    receivingId: r.receiving_invoice_id || null, // for View column links
    receivingInvoiceNumber: r.receiving_invoice_number || null,
    children: null,
  }));
}

// -------------------- Nested expandable table --------------------
function ParentsTable({
  level,
  rows,
  byLot, // only used at level 0 for "Lots"
  fetchParents, // async (wipId) => { ok, parents[] }
  titleRight, // optional header right content
}) {
  const [data, setData] = useState(rows || []);
  const [expandedRows, setExpandedRows] = useState({});
  const totals = useMemo(
    () => ({
      incomingKg: sum(data, (r) => r.incomingKg),
      incomingPcs: sum(data, (r) => r.incomingPcs),
      outgoingKg: sum(data, (r) => r.outgoingKg),
      outgoingPcs: sum(data, (r) => r.outgoingPcs),
    }),
    [data]
  );

  useEffect(() => setData(rows || []), [rows]);

  const onRowExpand = async (e) => {
    const row = e.data;
    // At level 0 we also show LOTS; at any level, for WIP we fetch its direct parents.
    if (row.children == null && row.kind === "wip" && row.id) {
      const res = await fetchParents(row.id);
      if (res?.ok) {
        const children = mapDeeperRows(res.parents);
        setData((prev) =>
          prev.map((r) => (r.key === row.key ? { ...r, children } : r))
        );
      }
    }
  };

  const rowExpansionTemplate = (row) => {
    return (
      <div className="px-2 py-3 space-y-4">
        {/* Parents of this WIP (any level, only for WIP rows) */}
        {row.kind === "wip" ? (
          <div>
            <div className="text-sm font-medium text-gray-700 mb-1">
              Parents of {row.label}
            </div>
            <ParentsTable
              level={level + 1}
              rows={row.children || []}
              byLot={null}
              fetchParents={fetchParents}
            />
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <DataTable
      value={data}
      dataKey="key"
      responsiveLayout="scroll"
      size="small"
      className="p-datatable-sm"
      expandedRows={expandedRows}
      onRowToggle={(e) => setExpandedRows(e.data)}
      onRowExpand={onRowExpand}
      rowExpansionTemplate={rowExpansionTemplate}
      header={
        level === 0 ? (
          <div className="flex justify-between items-center pr-2">
            {/* <div className="text-sm font-medium text-gray-700">Parents</div> */}
            {titleRight ? (
              <div className="text-xs text-gray-500">{titleRight}</div>
            ) : null}
          </div>
        ) : null
      }
      footer={
        <div className="flex justify-end gap-6 text-sm">
          <div>
            <span className="text-gray-600">Outgoing (kg): </span>
            <span className="font-semibold">{kgFmt(totals.incomingKg)}</span>
          </div>
          <div>
            <span className="text-gray-600">Outgoing (pcs): </span>
            <span className="font-semibold">{pcsFmt(totals.incomingPcs)}</span>
          </div>
          <div>
            <span className="text-gray-600">Incoming (kg): </span>
            <span className="font-semibold">{kgFmt(totals.outgoingKg)}</span>
          </div>
          <div>
            <span className="text-gray-600">Incoming (pcs): </span>
            <span className="font-semibold">{pcsFmt(totals.outgoingPcs)}</span>
          </div>
        </div>
      }
      emptyMessage="No parents."
    >
      <Column expander style={{ width: "3rem" }} />
      <Column
        header="Source"
        body={(r) => (
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">{r.label}</span>
              <span className="text-xs text-gray-500">
                {r.kind === "wip" ? "WIP" : "Receiving"}
              </span>
            </div>
            {r.kind === "wip"
              ? null
              : r.receivingInvoiceNumber && (
                  <span className="text-xs text-gray-500">
                    Invoice: {r.receivingInvoiceNumber}
                  </span>
                )}
          </div>
        )}
      />
      <Column
        header="Outgoing (kg)"
        body={(r) => kgFmt(r.incomingKg)}
        style={{ minWidth: 140 }}
      />
      <Column
        header="Outgoing (pcs)"
        body={(r) => pcsFmt(r.incomingPcs)}
        style={{ minWidth: 120 }}
      />
      <Column
        header="Incoming (kg)"
        body={(r) => (r.outgoingKg == null ? "—" : kgFmt(r.outgoingKg))}
        style={{ minWidth: 180 }}
      />
      <Column
        header="Incoming (pcs)"
        body={(r) => (r.outgoingPcs == null ? "—" : pcsFmt(r.outgoingPcs))}
        style={{ minWidth: 180 }}
      />
      <Column
        header="View"
        body={(r) => {
          if (r.kind === "wip" && r.id) {
            return (
              <button
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                onClick={() => window.open(`/wips/${r.id}`, "_blank")}
              >
                View
              </button>
            );
          } else if (r.kind === "receiving") {
            // Extract receiving_id from the row data - we need to pass this through the mapping
            const receivingId = r.receivingId;
            if (receivingId) {
              return (
                <button
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  onClick={() =>
                    window.open(`/receiving/${r.receivingId}`, "_blank")
                  }
                >
                  View
                </button>
              );
            }
          }
          return <span className="text-gray-400 text-sm">—</span>;
        }}
        style={{ minWidth: 120 }}
      />
    </DataTable>
  );
}

// -------------------- Manufactured Product Traceability Component --------------------
export default function ManufacturedProductTraceability({
  lookup,
  creditReturn,
  creditReturnItems,
  currentWip,
  incomingPlus,
  selectedPlu,
  setSelectedPlu,
  loadPluDetails,
  loadDirectParents,
  byParent,
  byLot,
  getSalesReport,
}) {
  const [rows, setRows] = useState([]);
  const [contextWipId, setContextWipId] = useState(null);
  const [contextLabel, setContextLabel] = useState("");

  const pluOptions = useMemo(() => {
    const base = [{ value: "", label: "— Choose PLU —" }];
    const dynamic = (incomingPlus || []).map((p) => ({
      value: p.plu || "",
      label: `${p.plu || "(blank)"} • ${kgFmt(p.incoming_kg)} • ${pcsFmt(
        p.incoming_pcs
      )} • parents:${p.parent_wip_count}`,
    }));
    return [...base, ...dynamic];
  }, [incomingPlus]);

  // Resolve WIP -> reset context
  useEffect(() => {
    if (!currentWip) {
      setRows([]);
      setContextWipId(null);
      setContextLabel("");
      return;
    }
    const rootLabel = `WIP ${currentWip.lot_number || ""}`.trim();
    setContextWipId(currentWip.id);
    setContextLabel(rootLabel);
    setRows([]);
  }, [currentWip]);

  // Seed first layer after PLU pick
  useEffect(() => {
    if (!selectedPlu || !byParent) return;
    setRows(mapFirstLayerRows(byParent));
  }, [selectedPlu, byParent]);

  // PLU pick once (Change to repick)
  const onPickPlu = async (plu) => {
    if (!plu || !currentWip?.id) return;
    setSelectedPlu(plu);
    const res = await loadPluDetails(currentWip.id, plu);
    if (res?.ok) setRows(mapFirstLayerRows(res.by_parent));
  };

  const onChangePlu = () => {
    setSelectedPlu(null);
    setRows([]);
    if (currentWip) {
      const rootLabel = `WIP ${currentWip.id} ${
        currentWip.lot_number || ""
      }`.trim();
      setContextWipId(currentWip.id);
      setContextLabel(rootLabel);
    }
  };

  // Provide fetchParents to nested tables
  const fetchParents = async (wipId) => {
    const res = await loadDirectParents(wipId);
    return res || { ok: false, parents: [] };
  };

  if (creditReturn) {
    return (
      <>
        <div className="px-4">
          <div className="card-custom bg-white p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="sm:flex-auto w-full">
                <div className="flex items-center gap-3">
                  <h2 className="text-base font-semibold leading-6 text-gray-900">
                    {creditReturn.name}
                  </h2>
                  <span
                    className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${
                      creditReturn.status === 1
                        ? "bg-green-50 text-green-700 ring-green-600/20"
                        : "bg-yellow-50 text-yellow-700 ring-yellow-600/20"
                    }`}
                  >
                    {creditReturn.status === 1 ? "Closed" : "Open"}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Customer: {creditReturn.customer_name}
                </p>
                {creditReturn.notes && (
                  <p className="text-sm text-gray-500 mt-1">
                    Notes: {creditReturn.notes}
                  </p>
                )}
              </div>
              <div className="shrink-0">
                <button
                  type="button"
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  onClick={() =>
                    window.open(`/credit-returns/${creditReturn.id}`, "_blank")
                  }
                >
                  View Credit Return
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-sm font-medium text-gray-600 mb-1">
                  Total Weight (kg)
                </div>
                <div className="text-xl font-semibold text-gray-900">
                  {crKgFmt(creditReturn.total_weight_kg)}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-sm font-medium text-gray-600 mb-1">
                  Total Weight (lbs)
                </div>
                <div className="text-xl font-semibold text-gray-900">
                  {lbsFmt(creditReturn.total_weight_lbs)}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-sm font-medium text-gray-600 mb-1">
                  Total Pieces
                </div>
                <div className="text-xl font-semibold text-gray-900">
                  {crPcsFmt(creditReturn.total_pieces)}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-sm font-medium text-gray-600 mb-1">
                  Total Cost
                </div>
                <div className="text-xl font-semibold text-gray-900">
                  {dollarFmt(creditReturn.total_cost)}
                </div>
              </div>
            </div>
          </div>
        </div>

        <section className="px-4">
          <div className="card-custom p-4">
            <div className="sm:flex-auto w-full mb-4">
              <h3 className="text-base font-semibold leading-6 text-gray-900">
                Credit Return Items
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                All items in credit return for lot number {lookup}
              </p>
            </div>
            <CreditReturnItemsTable items={creditReturnItems} />
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      {/* WIP Information Card */}
      {currentWip && (
        <div className="px-4">
          <div className="card-custom bg-white p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-2xl font-semibold text-gray-900">
                  WIP {currentWip.lot_number}
                </h2>
              </div>
              <div className="flex gap-2">
                <button
                  className="rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  onClick={() =>
                    window.open(`/wips/${currentWip.id}`, "_blank")
                  }
                >
                  View
                </button>
                <button
                  className="rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  onClick={() => getSalesReport(currentWip.lot_number)}
                >
                  Sales Report
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm font-medium text-gray-600 mb-1">
                  Total Outgoing Weight (kg)
                </div>
                <div className="text-xl font-semibold text-gray-900">
                  {kgFmt(currentWip.total_weight_in_kg)}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm font-medium text-gray-600 mb-1">
                  Total Outgoing Piece Count
                </div>
                <div className="text-xl font-semibold text-gray-900">
                  {pcsFmt(currentWip.total_piece_count)}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm font-medium text-gray-600 mb-1">
                  Start Date
                </div>
                <div className="text-xl font-semibold text-gray-900">
                  {formatDate(currentWip.created_at)}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <SelectMenu
                handleInputChange={(e) => {
                  console.log(e.target.value.value);
                  onPickPlu(e.target.value.value);
                }}
                name="plu_selection"
                options={pluOptions}
                parentClass="rounded-md w-80"
                placeholder="— Choose PLU —"
                value={pluOptions.find(
                  (opt) => opt.value === (selectedPlu || "")
                )}
              />
            </div>
          </div>
        </div>
      )}

      {/* Root table (expand for lots + recursive parents) */}
      {currentWip && (
        <section className="px-4">
          <div className="card-custom p-2">
            <ParentsTable
              level={0}
              rows={rows}
              byLot={byLot}
              fetchParents={fetchParents}
              titleRight=""
            />
          </div>
        </section>
      )}
    </>
  );
}

